import React from 'react';
import { SelectProps } from '../../types';

const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  label,
  error,
  help,
  disabled = false,
  required = false,
  className = '',
  id,
}) => {
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
  
  const baseClasses = 'w-full border rounded-md transition-all duration-normal focus:outline-none focus:ring-2 focus:ring-offset-0 appearance-none bg-white';
  
  const sizeClasses = 'px-4 py-3 text-base';
  
  const stateClasses = error
    ? 'border-error-300 focus:border-error-500 focus:ring-error-500'
    : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500';
  
  const disabledClasses = disabled
    ? 'bg-neutral-50 text-neutral-500 cursor-not-allowed'
    : 'text-neutral-900';
  
  const selectClasses = `
    ${baseClasses}
    ${sizeClasses}
    ${stateClasses}
    ${disabledClasses}
    ${className}
  `.trim();
  
  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-medium text-neutral-700 mb-2"
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <select
          id={selectId}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={disabled}
          required={required}
          className={selectClasses}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error ? `${selectId}-error` : help ? `${selectId}-help` : undefined
          }
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Icône de flèche personnalisée */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <svg
            className="w-5 h-5 text-neutral-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      
      {error && (
        <p
          id={`${selectId}-error`}
          className="text-error-600 text-sm mt-1"
          role="alert"
        >
          {error}
        </p>
      )}
      
      {help && !error && (
        <p
          id={`${selectId}-help`}
          className="text-neutral-500 text-sm mt-1"
        >
          {help}
        </p>
      )}
    </div>
  );
};

export default Select;
